"use client";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { usePathname } from "next/navigation";

interface HeaderHeightContextValue {
  headerHeight: number;
  hasStorefrontHeader: boolean;
  headerContentRef: React.RefObject<HTMLElement | null>;
  recalculateHeight: (options?: { resetPreviousHeight?: boolean }) => void;
  previousHeight: number;
}

export const HeaderHeightContext =
  createContext<HeaderHeightContextValue | null>(null);

export const NavigationProvider = ({
  children,
}: Readonly<{
  children: ReactNode;
}>) => {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [hasStorefrontHeader, setHasStorefrontHeader] = useState(false);
  const headerContentRef = useRef<HTMLElement>(null);
  const previousHeightRef = useRef(0);
  const pathname = usePathname();

  useEffect(() => {
    if (typeof ResizeObserver !== "undefined" && headerContentRef.current) {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        const newHeight =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;

        if (newHeight > previousHeightRef.current) {
          previousHeightRef.current = newHeight;
        }
        setHeaderHeight(newHeight);
      });

      observer.observe(headerContentRef.current);

      return () => observer.disconnect();
    }
  }, [pathname]);

  /**
   * Forces a recalculation and update of the header height.
   *
   * This function is typically used when the header content changes in a way
   * that the `ResizeObserver` might not immediately detect or if a manual
   * update is required.
   *
   * @returns {void}
   */
  const recalculateHeight = useCallback(
    (options?: { resetPreviousHeight?: boolean }) => {
      if (headerContentRef.current) {
        const currentHeight =
          headerContentRef.current.getBoundingClientRect().height;

        previousHeightRef.current = options?.resetPreviousHeight
          ? currentHeight
          : Math.max(previousHeightRef.current, currentHeight);
        setHeaderHeight(currentHeight);
      }
    },
    []
  );

  useEffect(() => {
    // Recheck height changes on navigation
    const headerElement = headerContentRef.current;
    setHasStorefrontHeader(Boolean(headerElement));

    if (headerElement) {
      const currentHeight =
        headerElement.getBoundingClientRect().height;

      previousHeightRef.current = currentHeight;
      setHeaderHeight(currentHeight);
    } else {
      previousHeightRef.current = 0;
      setHeaderHeight(0);
    }
  }, [pathname]);

  const contextValue = useMemo(
    () => ({
      headerHeight,
      hasStorefrontHeader,
      headerContentRef,
      recalculateHeight,
      previousHeight: previousHeightRef.current,
    }),
    [headerHeight, hasStorefrontHeader, recalculateHeight]
  );

  return (
    <HeaderHeightContext.Provider value={contextValue}>
      {children}
    </HeaderHeightContext.Provider>
  );
};

export const useHeaderHeight = () => {
  const context = useContext(HeaderHeightContext);
  if (!context) {
    throw new Error("useHeaderHeight must be used within NavigationProvider");
  }
  return context;
};
